import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { Router } from '@angular/router'
import { CommonModule } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import {
  TemplateItem,
  TemplateItemWithExpandedState,
  TemplateContentBlockType,
  TemplateContentBlockSource,
  PendingTemplateContentBlockSource,
  CompletedTemplateContentBlockSource,
  TemplateContentBlockSourceType,
  FlattenedTemplateItem,
  FormattedTextBlock,
  TemplateContentBlock,
  DiscussionSummaryContentBlock,
} from './types/template.types'
import { CallResource, MinutesAnonymization, MinutesVerboseness } from '../calls.service'
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { TemplateItemComponent, FileUploadRequest, PdfPreviewRequest } from './template-item/template-item.component'
import { PdfPreviewDialogComponent, PdfPreviewDialogData } from './pdf-preview-dialog/pdf-preview-dialog.component'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK } from './template-item-content-block/template-item-content-block.component'
import { ToastService } from '../toast.service'

type ActionName =
  | 'INDENT'
  | 'OUTDENT'
  | 'ADD_ITEM'
  | 'ADD_SUB_ITEM'
  | 'DUPLICATE'
  | 'REMOVE'
  | 'EXPAND_ALL'
  | 'COLLAPSE_ALL'
  | 'REVERT'
  | 'ERASE_ALL'
  | 'LOAD_TEMPLATE'
  | 'FULLSCREEN'
type ActionDefinition = {
  name: ActionName
  width: number
}
const ICON_BUTTON_WIDTH = 40
const GAP_WIDTH = 10

const actions: { [action: string]: ActionDefinition } = {
  INDENT: { name: 'INDENT', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  OUTDENT: { name: 'OUTDENT', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  ADD_ITEM: { name: 'ADD_ITEM', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  ADD_SUB_ITEM: { name: 'ADD_SUB_ITEM', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  DUPLICATE: { name: 'DUPLICATE', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  REMOVE: { name: 'REMOVE', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  EXPAND_ALL: { name: 'EXPAND_ALL', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  COLLAPSE_ALL: { name: 'COLLAPSE_ALL', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  REVERT: { name: 'REVERT', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  ERASE_ALL: { name: 'ERASE_ALL', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  LOAD_TEMPLATE: { name: 'LOAD_TEMPLATE', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
  FULLSCREEN: { name: 'FULLSCREEN', width: ICON_BUTTON_WIDTH + GAP_WIDTH },
}

const actionPriority: ActionName[] = [
  'ADD_ITEM',
  'ADD_SUB_ITEM',
  'REMOVE',
  'REVERT',
  'ERASE_ALL',
  'LOAD_TEMPLATE',
  'FULLSCREEN',
  'INDENT',
  'OUTDENT',
  'EXPAND_ALL',
  'COLLAPSE_ALL',
  'DUPLICATE',
]

const SAVE_ACTIONS_WIDTH = 140
const OVERFLOW_MENU_BUTTON_WIDTH = ICON_BUTTON_WIDTH + GAP_WIDTH
const MENU_BASE_WIDTH = 16

@Component({
  selector: 'app-template-editor',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, DragDropModule, TranslateModule, TemplateItemComponent],
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
})
export class TemplateEditorComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true })
  set templateItems(items: TemplateItem[]) {
    if (this.hasUnsavedChanges) {
      return
    }
    this.savedTemplateItems = items
    const expandedStates = this.collectExpandedStates(this._templateItems)
    this._templateItems = this.applyExpandedStates(items, expandedStates)
    this.initializeTemporaryIdCounter(items)
    this.templateItemsChanged.emit(this._templateItems)
  }

  @Input({ required: true })
  availableResources!: CallResource[]

  @Input({ required: true })
  allowFileUpload!: boolean

  @Input({ required: true })
  attendeeNames!: string[]

  @Input()
  superModeEnabled = false

  @Input()
  isFullscreenModeEnabled = false

  @Input()
  readonly = false

  @Input()
  showProcessingStatus = false

  @Input()
  allowLoadTemplate = false

  @Input({ required: true })
  defaultLevelOfDetail!: MinutesVerboseness

  @Input({ required: true })
  defaultAnonymization!: MinutesAnonymization

  @Output()
  templateSaved = new EventEmitter<TemplateItem[]>()

  @Output()
  fileUploadRequested = new EventEmitter<FileUploadRequest>()

  @Output()
  templateItemsChanged = new EventEmitter<TemplateItem[]>()

  @Output()
  fullscreenRequested = new EventEmitter<void>()

  @Output()
  exitFullscreenRequested = new EventEmitter<void>()

  @Output()
  loadTemplateRequested = new EventEmitter<void>()

  @Output()
  saveAsRequested = new EventEmitter<TemplateItem[]>()

  @ViewChild('topMenu', { static: false })
  topMenu!: ElementRef<HTMLDivElement>
  @ViewChildren(TemplateItemComponent)
  templateItemComponents!: QueryList<TemplateItemComponent>

  private savedTemplateItems: TemplateItem[] = []
  private router = inject(Router)
  private elementRef = inject(ElementRef)
  translateService = inject(TranslateService)
  toastService = inject(ToastService)
  dialog = inject(MatDialog)
  _templateItems: TemplateItemWithExpandedState[] = []
  selectedItemId: number | null = null
  hasUnsavedChanges = false
  displayUploadButton = false
  TemplateContentBlockType = TemplateContentBlockType
  TemplateContentBlockSourceType = TemplateContentBlockSourceType
  MinutesVerboseness = MinutesVerboseness
  MinutesAnonymization = MinutesAnonymization
  private changeDetector = inject(ChangeDetectorRef)
  private nextTemporaryId = -1
  private currentWidth: number = Infinity
  private resizeObserver: ResizeObserver | null = null
  actions = actions

  private getNextTemporaryId(): number {
    return this.nextTemporaryId--
  }

  private initializeTemporaryIdCounter(items: TemplateItem[]): void {
    const findMin = (items: TemplateItem[]): number => items.reduce((min, item) => Math.min(min, item.id, findMin(item.subitems)), Infinity)
    const minId = findMin(items)
    if (minId !== Infinity) {
      this.nextTemporaryId = minId - 1
    }
  }

  get templateItems(): TemplateItem[] {
    return this._templateItems
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(([entry]) => {
      if (entry?.target === this.topMenu.nativeElement) {
        this.currentWidth = entry.contentRect.width
        this.changeDetector.markForCheck()
      }
    })
    this.resizeObserver.observe(this.topMenu.nativeElement)
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect()
  }

  get activeActions(): ActionName[] {
    const availableWidth = this.currentWidth - SAVE_ACTIONS_WIDTH
    const initialWidth = MENU_BASE_WIDTH + OVERFLOW_MENU_BUTTON_WIDTH

    const filteredPriority = actionPriority.filter((actionName) => {
      if (actionName === 'LOAD_TEMPLATE' && !this.allowLoadTemplate) {
        return false
      }
      return true
    })

    return filteredPriority
      .map((actionName) => actions[actionName]!)
      .reduce(
        (acc, action) => {
          const newWidth = acc.width + action.width
          return availableWidth > newWidth ? { actions: [...acc.actions, action.name], width: newWidth } : acc
        },
        { actions: [] as ActionName[], width: initialWidth }
      ).actions
  }

  isActive(actionName: ActionName): boolean {
    return this.activeActions.includes(actionName)
  }

  get displayMenuButton(): boolean {
    const filteredPriorityLength = actionPriority.filter((actionName) => {
      if (actionName === 'LOAD_TEMPLATE' && !this.allowLoadTemplate) {
        return false
      }
      return true
    }).length
    return this.activeActions.length !== filteredPriorityLength
  }

  saveTemplate() {
    const cleanedItems = this.removeOrphanedSources(this._templateItems)
    this.templateSaved.emit(cleanedItems)
  }

  saveAsTemplate() {
    const cleanedItems = this.removeOrphanedSources(this._templateItems)
    this.saveAsRequested.emit(cleanedItems)
  }

  confirmSaveSuccess() {
    this.hasUnsavedChanges = false
    this.savedTemplateItems = this._templateItems
  }

  clearUnsavedChanges() {
    this.hasUnsavedChanges = false
  }

  getCleanedTemplateItems(): TemplateItem[] {
    return this.removeOrphanedSources(this._templateItems)
  }

  markAsChanged() {
    this.hasUnsavedChanges = true
    this.templateItemsChanged.emit(this._templateItems)
  }

  revertToLastSaved() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('TEMPLATES.EDITOR.REVERT_DIALOG.TITLE'),
        description: this.translateService.instant('TEMPLATES.EDITOR.REVERT_DIALOG.DESCRIPTION'),
        confirmLabel: this.translateService.instant('TEMPLATES.EDITOR.REVERT_DIALOG.CONFIRM'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const expandedStates = this.collectExpandedStates(this._templateItems)
        this._templateItems = this.applyExpandedStates(this.savedTemplateItems, expandedStates)
        this.selectedItemId = null
        this.hasUnsavedChanges = false
        this.changeDetector.detectChanges()
      }
    })
  }

  eraseAllItems() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('TEMPLATES.EDITOR.ERASE_ALL_DIALOG.TITLE'),
        description: this.translateService.instant('TEMPLATES.EDITOR.ERASE_ALL_DIALOG.DESCRIPTION'),
        confirmLabel: this.translateService.instant('TEMPLATES.EDITOR.ERASE_ALL_DIALOG.CONFIRM'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this._templateItems = []
        this.selectedItemId = null
        this.markAsChanged()
        this.changeDetector.detectChanges()
      }
    })
  }

  loadExistingTemplate() {
    this.loadTemplateRequested.emit()
  }

  get flattenedItems(): FlattenedTemplateItem[] {
    const topLevelItems = this.getSiblings(null).sort((a, b) => a.index - b.index)
    return this.flattenItemsRecursive(topLevelItems, 0, '')
  }

  selectItem(itemId: number) {
    if (this.selectedItemId === itemId) {
      this.selectedItemId = null
    } else {
      this.selectedItemId = itemId
      this.router.navigate([], {
        queryParams: { templateItem: itemId },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    }
  }

  deselectItem(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.selectedItemId = null
    }
  }

  private collectExpandedStates(items: TemplateItemWithExpandedState[], path = ''): Map<string, boolean> {
    return items.reduce((states, item, index) => {
      const itemPath = path ? `${path}.${index}` : String(index)
      states.set(itemPath, item.expanded)
      if (item.subitems.length > 0) {
        const childStates = this.collectExpandedStates(item.subitems, itemPath)
        childStates.forEach((value, key) => states.set(key, value))
      }
      return states
    }, new Map<string, boolean>())
  }

  private applyExpandedStates(items: TemplateItem[], expandedStates: Map<string, boolean>, path = ''): TemplateItemWithExpandedState[] {
    return items.map((item, index) => {
      const itemPath = path ? `${path}.${index}` : String(index)
      return {
        ...item,
        expanded: expandedStates.get(itemPath) ?? false,
        subitems: this.applyExpandedStates(item.subitems, expandedStates, itemPath),
      }
    })
  }

  private flattenItemsRecursive(items: TemplateItemWithExpandedState[], indentLevel: number, parentNumber: string): FlattenedTemplateItem[] {
    return items.flatMap((item, index) => {
      const itemNumber = parentNumber ? `${parentNumber}.${index + 1}` : `${index + 1}`
      // TODO avoid casting, cannot use spread because it breaks the object reference
      const flattenedItem = item as FlattenedTemplateItem
      flattenedItem.indentLevel = indentLevel
      flattenedItem.itemNumber = itemNumber
      if (item.subitems.length > 0) {
        const sortedSubitems = [...item.subitems].sort((a, b) => a.index - b.index)
        return [flattenedItem, ...this.flattenItemsRecursive(sortedSubitems, indentLevel + 1, itemNumber)]
      }
      return [flattenedItem]
    })
  }

  addNewItem() {
    const selectedItem = this.selectedItemId !== null ? this.findItemById(this._templateItems, this.selectedItemId) : null
    const parentId = selectedItem !== null ? selectedItem.parentId : null
    const newIndex = selectedItem !== null ? selectedItem.index + 1 : this.calculateNewIndex(parentId)

    const newItem = this.createNewItem(parentId, newIndex, this.translateService.instant('TEMPLATES.EDITOR.NEW_ITEM_TITLE'))
    if (selectedItem !== null) {
      this._templateItems = this.insertItemAfter(this._templateItems, selectedItem.id, newItem, parentId)
    } else {
      this._templateItems = [...this._templateItems, newItem]
    }
    this.selectedItemId = newItem.id
    this.markAsChanged()
    this.scrollToItem(newItem.id)
    this.startEditingTitleForItem(newItem.id)
  }

  addSubItem() {
    if (this.selectedItemId === null) {
      return
    }
    const parentItem = this.findItemById(this._templateItems, this.selectedItemId)
    if (parentItem === null) {
      return
    }
    const maxIndex = parentItem.subitems.length > 0 ? Math.max(...parentItem.subitems.map((item) => item.index)) : -1

    const newItem = this.createNewItem(this.selectedItemId, maxIndex + 1, this.translateService.instant('TEMPLATES.EDITOR.NEW_SUBITEM_TITLE'))
    this._templateItems = this.addSubitemToParent(this._templateItems, this.selectedItemId!, newItem)
    this.selectedItemId = newItem.id
    this.markAsChanged()
    this.scrollToItem(newItem.id)
    this.startEditingTitleForItem(newItem.id)
  }

  private startEditingTitleForItem(itemId: number) {
    const component = this.templateItemComponents.find((component) => component.item.id === itemId) ?? null
    if (component !== null) {
      component.startEditingTitle()
    }
  }

  private createNewItem(parentId: number | null, index: number, title: string): TemplateItemWithExpandedState {
    const newItemId = this.getNextTemporaryId()
    const newBlockId = this.getNextTemporaryId()
    const transcriptSource: CompletedTemplateContentBlockSource = {
      type: TemplateContentBlockSourceType.TRANSCRIPT,
      callResourceId: null,
      callResource: null,
      templateContentBlockId: newBlockId,
      isPending: false,
    }
    const discussionSummaryBlock: DiscussionSummaryContentBlock = {
      id: newBlockId,
      templateItemId: newItemId,
      index: 0,
      type: TemplateContentBlockType.DISCUSSION_SUMMARY,
      sources: [transcriptSource],
      data: { guidance: '' },
      levelOfDetail: this.defaultLevelOfDetail,
      anonymization: this.defaultAnonymization,
      category: null,
    }

    return {
      id: newItemId,
      index,
      title,
      presenters: [],
      contentBlocks: [discussionSummaryBlock],
      subitems: [],
      duration: null,
      parentId,
      expanded: true,
      minuteTimelineSegments: [],
      hasMinuteContent: false,
    }
  }

  scrollToItem(itemId: number) {
    this.changeDetector.detectChanges()
    setTimeout(() => {
      const element = document.getElementById(`template-item-${itemId}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 0)
  }

  duplicateItem() {
    if (this.selectedItemId === null) {
      return
    }
    const itemToDuplicate = this.findItemById(this._templateItems, this.selectedItemId)
    if (itemToDuplicate === null) {
      return
    }

    const newTitle = `${itemToDuplicate.title} (Copy)`
    const duplicatedItem = this.deepCopyItemWithNewIds(itemToDuplicate, newTitle, null)

    this._templateItems = this.insertItemAfter(this._templateItems, this.selectedItemId, duplicatedItem, itemToDuplicate.parentId)
    this.selectedItemId = duplicatedItem.id
    this.markAsChanged()
    this.scrollToItem(duplicatedItem.id)
  }

  private insertItemAfter(
    items: TemplateItemWithExpandedState[],
    referenceItemId: number,
    newItem: TemplateItemWithExpandedState,
    parentId: number | null
  ): TemplateItemWithExpandedState[] {
    if (parentId === null) {
      const itemIndex = items.findIndex((item) => item.id === referenceItemId)
      const newItems = [...items.slice(0, itemIndex + 1), newItem, ...items.slice(itemIndex + 1)]
      return this.reindexItems(newItems.filter((item) => item.parentId === null))
    }

    return items.map((item) => {
      if (item.id === parentId) {
        const itemIndex = item.subitems.findIndex((subitem) => subitem.id === referenceItemId)
        const newSubitems = [...item.subitems.slice(0, itemIndex + 1), newItem, ...item.subitems.slice(itemIndex + 1)] as TemplateItemWithExpandedState[]
        return { ...item, subitems: this.reindexItems(newSubitems) }
      }
      if (item.subitems.length > 0) {
        return { ...item, subitems: this.insertItemAfter(item.subitems, referenceItemId, newItem, parentId) }
      }
      return item
    })
  }

  private reindexItems(items: TemplateItemWithExpandedState[]): TemplateItemWithExpandedState[] {
    return items.map((item, index) => ({ ...item, index }))
  }

  removeItem() {
    if (this.selectedItemId === null) {
      return
    }
    const itemToRemove = this.findItemById(this._templateItems, this.selectedItemId)
    if (itemToRemove === null) {
      return
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('TEMPLATES.EDITOR.REMOVE_ITEM_DIALOG.TITLE'),
        description: this.translateService.instant('TEMPLATES.EDITOR.REMOVE_ITEM_DIALOG.DESCRIPTION', { title: itemToRemove.title }),
        confirmLabel: this.translateService.instant('COMMON.DELETE'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return
      }
      this._templateItems = this.removeItemById(this._templateItems, this.selectedItemId!)
      this.selectedItemId = null
      this.markAsChanged()
      this.changeDetector.detectChanges()
    })
  }

  onItemDrop(event: CdkDragDrop<TemplateItem[]>) {
    if (event.previousIndex === event.currentIndex) {
      return
    }
    const flatItems = this.flattenedItems
    const movedItem = flatItems[event.previousIndex]
    const targetItem = flatItems[event.currentIndex]
    if (movedItem.parentId !== targetItem.parentId) {
      return
    }
    const parentId = movedItem.parentId
    const siblings = this.getSiblings(parentId)
    const oldIndex = siblings.findIndex((s) => s.id === movedItem.id)
    const newIndex = siblings.findIndex((s) => s.id === targetItem.id)
    if (oldIndex === -1 || newIndex === -1) {
      return
    }
    moveItemInArray(siblings, oldIndex, newIndex)
    siblings.forEach((item, idx) => {
      item.index = idx
    })
    this.markAsChanged()
  }

  indentItem() {
    if (this.selectedItemId === null) {
      return
    }
    const currentIndex = this.flattenedItems.findIndex((item) => item.id === this.selectedItemId)
    if (currentIndex <= 0) {
      return
    }
    const prevItem = this.flattenedItems[currentIndex - 1]
    const itemToMove = this.findItemById(this._templateItems, this.selectedItemId)
    if (itemToMove === null) {
      return
    }
    const newParent = this.findItemById(this._templateItems, prevItem.id)
    if (newParent === null) {
      return
    }
    if (itemToMove.parentId === null) {
      this._templateItems.splice(this._templateItems.indexOf(itemToMove), 1)
    } else {
      const oldParent = this.findItemById(this._templateItems, itemToMove.parentId)!
      oldParent.subitems.splice(oldParent.subitems.indexOf(itemToMove), 1)
    }
    itemToMove.parentId = prevItem.id
    newParent.subitems.push(itemToMove)
    newParent.subitems.forEach((item, idx) => {
      item.index = idx
    })
    this.markAsChanged()
  }

  outdentItem() {
    if (this.selectedItemId === null) {
      return
    }
    const itemToMove = this.findItemById(this._templateItems, this.selectedItemId)
    if (itemToMove === null || itemToMove.parentId === null) {
      return
    }
    const currentParent = this.findItemById(this._templateItems, itemToMove.parentId)
    if (currentParent === null) {
      return
    }
    const itemIndex = currentParent.subitems.findIndex((item) => item.id === this.selectedItemId)
    if (itemIndex !== -1) {
      currentParent.subitems.splice(itemIndex, 1)
      currentParent.subitems.forEach((item, idx) => {
        item.index = idx
      })
    }
    if (currentParent.parentId !== null) {
      const newParent = this.findItemById(this._templateItems, currentParent.parentId)
      if (newParent === null) {
        return
      }
      itemToMove.parentId = currentParent.parentId
      const insertIndex = newParent.subitems.indexOf(currentParent) + 1
      newParent.subitems.splice(insertIndex, 0, itemToMove)
      newParent.subitems.forEach((item, idx) => {
        item.index = idx
      })
    } else {
      itemToMove.parentId = null
      const insertIndex = this._templateItems.indexOf(currentParent) + 1
      this._templateItems.splice(insertIndex, 0, itemToMove)
      this._templateItems
        .filter((item) => item.parentId === null)
        .forEach((item, idx) => {
          item.index = idx
        })
    }
    this.markAsChanged()
  }

  canIndent(): boolean {
    if (this.selectedItemId === null) {
      return false
    }
    const currentIndex = this.flattenedItems.findIndex((item) => item.id === this.selectedItemId)
    if (currentIndex <= 0) {
      return false
    }
    const currentItem = this.flattenedItems[currentIndex]
    const prevItem = this.flattenedItems[currentIndex - 1]
    return currentItem.parentId === prevItem.parentId
  }

  canOutdent(): boolean {
    if (this.selectedItemId === null) {
      return false
    }
    const item = this.findItemById(this._templateItems, this.selectedItemId)
    return item !== null ? item.parentId !== null : false
  }

  expandAll(): void {
    this.setExpandedStateForAll(this._templateItems, true)
  }

  collapseAll(): void {
    this.setExpandedStateForAll(this._templateItems, false)
  }

  get hasAnyExpandedItems(): boolean {
    const checkItems = (items: TemplateItemWithExpandedState[]): boolean => items.some((item) => item.expanded || checkItems(item.subitems))
    return checkItems(this._templateItems)
  }

  private setExpandedStateForAll(items: TemplateItemWithExpandedState[], expanded: boolean): void {
    items.forEach((item) => {
      item.expanded = expanded
      if (item.subitems.length > 0) {
        this.setExpandedStateForAll(item.subitems, expanded)
      }
    })
  }

  getNonOrphanSources(block: TemplateContentBlock): TemplateContentBlockSource[] {
    return block.sources.filter((source) => {
      if (source.isPending === true) {
        return false
      }
      if (source.type === TemplateContentBlockSourceType.TRANSCRIPT) {
        return true
      }
      return source.callResourceId !== null && this.availableResources.some((r) => r.id === source.callResourceId)
    })
  }

  addPendingSourceToBlock(blockId: number, fileName: string) {
    const allBlocks = this.getAllContentBlocks(this._templateItems)
    const block = allBlocks.find((b) => b.id === blockId) ?? null
    if (block === null) {
      return
    }
    const pendingSource: PendingTemplateContentBlockSource = {
      type: TemplateContentBlockSourceType.FILE_UPLOAD,
      templateContentBlockId: blockId,
      callResourceId: null,
      callResource: null,
      isPending: true,
      fileName,
    }
    block.sources.push(pendingSource)
    this.markAsChanged()
  }

  removePendingSourceFromBlock(blockId: number, fileName: string) {
    const allBlocks = this.getAllContentBlocks(this._templateItems)
    const block = allBlocks.find((b) => b.id === blockId) ?? null
    if (block === null) {
      return
    }
    block.sources = block.sources.filter((source) => !(source.isPending && source.fileName === fileName))
    this.markAsChanged()
  }

  addSourceToBlock(blockId: number, resource: CallResource, fileName?: string) {
    const allBlocks = this.getAllContentBlocks(this._templateItems)
    const block = allBlocks.find((b) => b.id === blockId) ?? null
    if (block === null) {
      return
    }
    if (fileName !== undefined) {
      block.sources = block.sources.filter((source) => !(source.isPending && source.fileName === fileName))
    }
    const totalNumberOfPagesOfSources = block.sources.reduce((total, source) => {
      if (
        !source.isPending &&
        source.type === TemplateContentBlockSourceType.FILE_UPLOAD &&
        source.callResource !== null &&
        source.callResource.metadata !== null
      ) {
        return total + source.callResource.metadata.numberOfPages
      }
      return total
    }, 0)
    if (totalNumberOfPagesOfSources + (resource.metadata?.numberOfPages ?? 0) > MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK) {
      this.toastService.showRaw(
        this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_PAGE_LIMIT_EXCEEDED', { maxPages: MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK })
      )
      return
    }
    const newSource: CompletedTemplateContentBlockSource = {
      type: TemplateContentBlockSourceType.FILE_UPLOAD,
      templateContentBlockId: blockId,
      callResourceId: resource.id,
      callResource: resource,
      isPending: false,
    }
    block.sources.push(newSource)
    this.markAsChanged()
  }

  private getAllContentBlocks(items: TemplateItemWithExpandedState[]): TemplateContentBlock[] {
    return items.flatMap((item) => [...item.contentBlocks, ...this.getAllContentBlocks(item.subitems)])
  }

  private removeOrphanedSources(items: TemplateItemWithExpandedState[]): TemplateItemWithExpandedState[] {
    return items.map((item) => ({
      ...item,
      contentBlocks: item.contentBlocks.map((block) => ({
        ...block,
        sources: this.getNonOrphanSources(block),
      })),
      subitems: item.subitems ? this.removeOrphanedSources(item.subitems) : [],
    }))
  }

  private calculateNewIndex(parentId: number | null): number {
    if (parentId !== null) {
      const parent = this.findItemById(this._templateItems, parentId)
      if (parent !== null && parent.subitems.length > 0) {
        return Math.max(...parent.subitems.map((item) => item.index)) + 1
      }
      return 0
    }
    const topLevelItems = this.getSiblings(null)
    return topLevelItems.length > 0 ? Math.max(...topLevelItems.map((item) => item.index)) + 1 : 0
  }

  private deepCopyItemWithNewIds(item: TemplateItemWithExpandedState, newTitle: string | null, newParentId: number | null): TemplateItemWithExpandedState {
    const newItemId = this.getNextTemporaryId()
    const copiedItem: TemplateItemWithExpandedState = {
      ...item,
      id: newItemId,
      title: newTitle !== null ? newTitle : item.title,
      parentId: newParentId !== null ? newParentId : item.parentId,
      expanded: item.expanded,
      contentBlocks: item.contentBlocks.map((block) => this.deepCopyContentBlock(block, newItemId)),
      subitems: item.subitems.map((subitem: TemplateItemWithExpandedState) => this.deepCopyItemWithNewIds(subitem, null, newItemId)),
    }
    return copiedItem
  }

  private deepCopyContentBlock(block: TemplateContentBlock, newItemId: number): TemplateContentBlock {
    const newBlockId = this.getNextTemporaryId()
    const baseBlock = {
      ...block,
      id: newBlockId,
      templateItemId: newItemId,
      sources: block.sources.map((source) => ({
        ...source,
        templateContentBlockId: newBlockId,
      })),
    }

    switch (block.type) {
      case TemplateContentBlockType.FIXED_TEXT:
        return {
          ...baseBlock,
          type: block.type,
          data: {
            formattedTextBlock: {
              content: block.data.formattedTextBlock.content,
              index: block.data.formattedTextBlock.index,
            },
          },
        }
      case TemplateContentBlockType.VARIABLE_TEXT:
        return {
          ...baseBlock,
          type: block.type,
          data: {
            formattedTextBlock: {
              content: block.data.formattedTextBlock.content,
              index: block.data.formattedTextBlock.index,
            },
          },
        }
      case TemplateContentBlockType.DISCUSSION_SUMMARY:
      case TemplateContentBlockType.KEY_MESSAGES:
      case TemplateContentBlockType.DISCUSSION_CHALLENGE:
        return {
          ...baseBlock,
          type: block.type,
          data: {
            guidance: block.data.guidance,
          },
        }
      case TemplateContentBlockType.BINARY:
        return {
          ...baseBlock,
          type: block.type,
          data: {
            formattedTextBlocks: block.data.formattedTextBlocks.map((ftb) => ({
              content: ftb.content,
              index: ftb.index,
            })),
          },
        }
      default:
        return baseBlock as TemplateContentBlock
    }
  }

  private findItemById(items: TemplateItemWithExpandedState[], id: number): TemplateItemWithExpandedState | null {
    const item = items.find((item) => item.id === id) ?? null
    if (item !== null) {
      return item
    }
    return items.map((item) => this.findItemById(item.subitems, id)).find((result) => result !== null) ?? null
  }

  private getSiblings(parentId: number | null): TemplateItemWithExpandedState[] {
    if (parentId !== null) {
      return this.findItemById(this._templateItems, parentId)?.subitems ?? []
    }
    return this._templateItems.filter((item) => item.parentId === null)
  }

  private removeItemById(items: TemplateItemWithExpandedState[], idToRemove: number): TemplateItemWithExpandedState[] {
    const filtered = items.filter((item) => item.id !== idToRemove)
    return filtered.map((item, index) => ({
      ...item,
      index,
      subitems: this.removeItemById(item.subitems, idToRemove),
    }))
  }

  private addSubitemToParent(
    items: TemplateItemWithExpandedState[],
    parentId: number,
    newItem: TemplateItemWithExpandedState
  ): TemplateItemWithExpandedState[] {
    return items.map((item) => {
      if (item.id === parentId) {
        return {
          ...item,
          subitems: [...(item.subitems ?? []), newItem],
        }
      }
      if (item.subitems && item.subitems.length > 0) {
        return {
          ...item,
          subitems: this.addSubitemToParent(item.subitems, parentId, newItem),
        }
      }
      return item
    })
  }

  openPdfPreview(request: PdfPreviewRequest): void {
    const rect = this.elementRef.nativeElement.getBoundingClientRect()
    const padding = 40

    const dialogConfig = this.isFullscreenModeEnabled
      ? {
          width: '70vw',
          maxWidth: '900px',
          height: '85vh',
        }
      : {
          width: `${rect.width - padding * 2}px`,
          maxWidth: '900px',
          height: `${rect.height - padding * 2}px`,
          position: {
            left: `${rect.left + padding}px`,
            top: `${rect.top + padding}px`,
          },
        }

    this.dialog.open(PdfPreviewDialogComponent, {
      ...dialogConfig,
      data: {
        downloadUrl: request.downloadUrl,
        fileName: request.fileName,
      } as PdfPreviewDialogData,
    })
  }
}
